import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useUpdateAppSettings } from '../../hooks/useBackend';
import useIsMobile from '../../hooks/useIsMobile';
import { MOBILE_UI } from '../../constants/mobileUi';
import { MARKET_INDEX_SYMBOLS } from '../../constants/marketIndices';
import {
  DEFAULT_RELATIVE_STRENGTH_SETTINGS,
  normalizeRelativeStrengthSettings,
  RELATIVE_STRENGTH_SETTINGS_KEY,
} from '../../constants/relativeStrengthSettings';
import { normalizeRsTimeframeKey } from '../../constants/relativeStrengthTimeframes';
import RelativeStrengthPanel from '../MarketOverview/RelativeStrengthPanel';

/**
 * Relative Strength page (desktop Tools + mobile Market tab).
 * Own timeframe / relative-to state (not shared with Index comparison).
 */
export default function MarketRelativeStrengthPage({
  methodologyOpen = null,
  onMethodologyOpenChange = null,
}) {
  const isMobile = useIsMobile();
  const { user } = useContext(AuthContext);
  const { updateAppSettings } = useUpdateAppSettings();

  const settings = useMemo(
    () => normalizeRelativeStrengthSettings(
      user?.appSettings?.[RELATIVE_STRENGTH_SETTINGS_KEY] || DEFAULT_RELATIVE_STRENGTH_SETTINGS,
    ),
    [user?.appSettings?.[RELATIVE_STRENGTH_SETTINGS_KEY]],
  );

  const [timeframe, setTimeframe] = useState(() => (
    normalizeRsTimeframeKey(settings.defaultTimeframe)
  ));
  const [relativeTo, setRelativeTo] = useState('SPY');

  useEffect(() => {
    if (settings.mode === 'single') {
      setTimeframe(normalizeRsTimeframeKey(settings.defaultTimeframe));
    }
  }, [settings.mode, settings.defaultTimeframe]);

  const handleSettingsChange = useCallback(async (nextSettings) => {
    const normalized = normalizeRelativeStrengthSettings(nextSettings);
    if (normalized.mode === 'single') {
      setTimeframe(normalizeRsTimeframeKey(normalized.defaultTimeframe));
    }
    const token = user?.token;
    if (!token) return;
    try {
      await updateAppSettings(token, { [RELATIVE_STRENGTH_SETTINGS_KEY]: normalized });
    } catch (err) {
      console.error('Failed to save relative strength settings:', err);
    }
  }, [user?.token, updateAppSettings]);

  const relativeToOptions = useMemo(() => {
    const custom = user?.appSettings?.customMarketOverviewTickers || [];
    return [...MARKET_INDEX_SYMBOLS, ...custom];
  }, [user?.appSettings?.customMarketOverviewTickers]);

  return (
    <div
      data-testid="market-relative-strength-page"
      style={{
        paddingTop: MOBILE_UI.pagePadding,
        paddingLeft: MOBILE_UI.pagePadding,
        paddingRight: MOBILE_UI.pagePadding,
        // On mobile the MarketPage shell owns the bottom-nav clearance.
        paddingBottom: isMobile ? MOBILE_UI.pagePadding : 24,
        height: isMobile ? undefined : '100%',
        minHeight: isMobile ? undefined : 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ flex: 1, minHeight: 0, overflow: isMobile ? 'visible' : 'hidden' }}>
        <RelativeStrengthPanel
          settings={settings}
          onSettingsChange={handleSettingsChange}
          timeframeKey={timeframe}
          onTimeframeChange={setTimeframe}
          relativeToOptions={relativeToOptions}
          relativeTo={relativeTo}
          onRelativeToChange={setRelativeTo}
          listMaxHeight={410}
          fillHeight={!isMobile}
          methodologyOpen={methodologyOpen}
          onMethodologyOpenChange={onMethodologyOpenChange}
        />
      </div>
    </div>
  );
}
